/**
 * SPEC.md §3, §5 — the source pane.
 *
 * M3.2 (issue #11) swaps the plain `<textarea>` for CodeMirror 6: line
 * numbers, active-line highlight, bracket matching, Tab/Shift-Tab indent,
 * and undo/redo via CodeMirror's own history — all composed extensions, not
 * reimplemented. CodeMirror itself is dynamically imported through
 * `codeMirrorSetup.ts` (rule 5's reasoning applies here, not just to
 * Mermaid — see that module's header for the numbers). M3.3 (issue #12)
 * attaches `mermaidLanguage.ts` for highlighting and folding. M3.4 (issue
 * #13) adds the error squiggly/gutter marker below.
 *
 * `Editor` owns the `EditorView` instance directly, the same
 * imperative-instance-behind-a-ref shape `App.tsx` uses for the render loop
 * and `Preview.tsx` uses for the viewport engine: created once in an effect,
 * torn down on unmount. It stays a controlled component from the outside —
 * an incoming `value` is only dispatched into the document when it actually
 * differs from the doc's current content, so the app's own state updates
 * (which are themselves driven by this component's onChange) never fight
 * the user's own typing or reset the cursor mid-edit.
 */
import { useEffect, useRef } from 'react';
import type { Compartment } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';
import { getCodeMirrorSetup } from '../lib/codeMirrorSetup';
import type { RenderError } from '../lib/render';
import { getVimMode, subscribeVimMode } from '../lib/vimMode';

interface EditorProps {
  value: string;
  onChange: (value: string) => void;
  error: RenderError | null;
}

export default function Editor({ value, onChange, error }: EditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Read at view-construction time, not closed over at mount — if `value`
  // changes externally (e.g. a future New/hash-load/Recent action) while the
  // dynamic import above is still in flight, the view must open with the
  // latest source, not whatever `value` happened to be when this effect was
  // first set up. Same reasoning applies to `error` below.
  const valueRef = useRef(value);
  valueRef.current = value;
  const errorRef = useRef(error);
  errorRef.current = error;
  const setErrorLineRef = useRef<Awaited<ReturnType<typeof getCodeMirrorSetup>>['setErrorLine'] | null>(null);
  const vimModeCompartmentRef = useRef<Compartment | null>(null);
  const vimExtensionRef = useRef<Awaited<ReturnType<typeof getCodeMirrorSetup>>['vim'] | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;

    getCodeMirrorSetup().then(({ EditorView, extensions, setErrorLine, vimModeCompartment, vim }) => {
      if (cancelled) return;
      setErrorLineRef.current = setErrorLine;
      vimModeCompartmentRef.current = vimModeCompartment;
      vimExtensionRef.current = vim;
      const view = new EditorView({
        doc: valueRef.current,
        extensions: [
          ...extensions,
          EditorView.updateListener.of((update) => {
            if (update.docChanged) onChangeRef.current(update.state.doc.toString());
          }),
        ],
        parent: container,
      });
      viewRef.current = view;
      view.dispatch({ effects: setErrorLine.of(errorRef.current?.line ?? null) });
      // Vim mode is a persisted user preference (vimMode.ts), not part of
      // this component's own props — read it directly at construction time,
      // same reasoning as valueRef/errorRef above: the dynamic import may
      // still be in flight by the time a stale closure would've captured it.
      if (getVimMode()) {
        view.dispatch({ effects: vimModeCompartment.reconfigure(vim()) });
      }
    });

    return () => {
      cancelled = true;
      viewRef.current?.destroy();
      viewRef.current = null;
    };
    // Created once — later `value`/`error` changes are reconciled by the
    // effects below, not by recreating the view; the initial doc and error
    // line both read refs (not values closed over at mount) so neither can
    // go stale while the dynamic import above is in flight; onChange is
    // read through onChangeRef for the same reason.
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === value) return;
    view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
  }, [value]);

  // Mirrors render.ts's error timing exactly (SPEC.md §3, already tested in
  // M1) — no new rule here, just a view onto it: error is written only by a
  // completed failed render and cleared only by a successful one, so this
  // effect firing on every `error` change reproduces that automatically.
  useEffect(() => {
    const view = viewRef.current;
    const setErrorLine = setErrorLineRef.current;
    if (!view || !setErrorLine) return;
    view.dispatch({ effects: setErrorLine.of(error?.line ?? null) });
  }, [error]);

  // Reconfigures the vim compartment (not a state prop — vimMode.ts's own
  // subscribe/notify store, same pattern InterfaceThemeToggle.tsx's toggle
  // uses) whenever it changes, from either VimModeToggle.tsx's button or the
  // command palette's equivalent entry. A no-op until the dynamic import
  // above resolves and populates the refs — the subscription is live from
  // mount, but there's nothing to reconfigure yet if it fires first.
  useEffect(() => {
    return subscribeVimMode((enabled) => {
      const view = viewRef.current;
      const compartment = vimModeCompartmentRef.current;
      const vim = vimExtensionRef.current;
      if (!view || !compartment || !vim) return;
      view.dispatch({ effects: compartment.reconfigure(enabled ? vim() : []) });
    });
  }, []);

  return (
    // `grow` (issue #37, M6.10): a no-op in App.tsx's normal grid layout
    // (grid items already stretch to fill their cell) but required once
    // App.tsx's responsive tab mode below 820px makes this section a
    // flex child instead — without it the pane would shrink to its
    // content's height rather than filling the space below the tab strip.
    //
    // `min-w-0` alongside `min-h-0`: this section is the real grid item
    // (its App.tsx wrapper is `display: contents`), and a flex/grid item's
    // default `min-width: auto` means the browser sizes the column to fit
    // its content's intrinsic width — one long, unwrapped Mermaid line (word
    // wrap is off by default, SPEC.md §5) blows the column past the
    // splitter's ratio entirely, and dragging the splitter narrower does
    // nothing because the content itself refuses to shrink. `.cm-scroller`'s
    // own `overflow: auto` (codeMirrorSetup.ts) can only take effect once
    // something upstream allows this box to shrink below that intrinsic
    // width in the first place.
    <section aria-label="Source" className="flex min-h-0 min-w-0 grow flex-col bg-ink">
      <div className="pane-label px-3 py-2.5 text-mute">Source</div>
      <div ref={containerRef} className="min-h-0 min-w-0 grow overflow-hidden" />
    </section>
  );
}
